import { useEffect, useRef } from 'react';

/**
 * Confirmation dialog for "Start fresh — reset this surah's progress".
 *
 * Destructive operation: clicking Reset deletes every ayah_reads row for
 * the user × surah. The trophy is lost until they complete the surah
 * again. We deliberately make the destructive button the SECONDARY
 * (red-ish) one and Cancel the primary so the safe action is the
 * default — Enter / Escape both keep the user safe.
 */
export function ResetSurahDialog({
  open,
  surahName,
  isResetting,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  surahName: string | undefined;
  isResetting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus Cancel by default so the safe action wins on accidental Enter.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isResetting) {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isResetting, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-dialog-title"
      className="
        fixed inset-0 z-50 flex items-center justify-center
        bg-night-950/80 backdrop-blur-sm
        motion-safe:animate-in motion-safe:fade-in
      "
      onClick={(e) => {
        if (e.target === e.currentTarget && !isResetting) onCancel();
      }}
    >
      <div
        className="
          relative mx-4 w-full max-w-sm
          rounded-2xl border border-night-500/40 bg-night-800 p-6
          shadow-2xl
        "
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="reset-dialog-title"
          className="font-display text-lg font-bold text-night-50"
        >
          Reset progress on {surahName ?? 'this surah'}?
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-night-200">
          This will reset your progress to 0% and hide the trophy. You
          can earn it back by completing the surah again. Your bookmarks
          and notes are untouched.
        </p>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
          {/* Cancel — primary (safe action) */}
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={isResetting}
            className="
              w-full rounded-lg
              bg-beam px-4 py-2.5 text-sm font-semibold uppercase tracking-wide text-beam-ink
              transition-colors hover:brightness-110
              disabled:opacity-50
              sm:w-auto
            "
          >
            Cancel
          </button>
          {/* Reset — destructive (secondary visual) */}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isResetting}
            className="
              w-full rounded-lg
              border border-red-500/40 bg-red-500/10 px-4 py-2.5
              text-sm font-semibold uppercase tracking-wide text-red-300
              transition-colors hover:bg-red-500/20
              disabled:cursor-not-allowed disabled:opacity-50
              sm:w-auto
            "
          >
            {isResetting ? 'Resetting…' : 'Reset progress'}
          </button>
        </div>
      </div>
    </div>
  );
}
